import React, { useState, useEffect, FormEvent } from "react";
import "./OtpPage.less";
import service from "../../services/service";
import { toast } from "react-toastify";
import FlyoutLayout from "../../shared/FlyoutLayout/FlyoutLayout";

interface OTPFormProps {
  isOpen: boolean;
  email: string;
  closeModal: () => void;
  setUser?: (user: any) => void;
  setToken?: (token: string) => void;
  onBackToForgot?: () => void;
  onProceedToReset?: () => void;
}

const OTPForm: React.FC<OTPFormProps> = ({
  isOpen,
  email,
  closeModal,
  setUser,
  setToken,
  onBackToForgot,
  onProceedToReset,
}) => {
  const displayName = "cmp-otpform";

  const [otp, setOtp] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [resendCooldown, setResendCooldown] = useState<number>(0);

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault();

    if (otp.length !== 6) {
      setError("Please enter the complete 6-digit code");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const data = await service.verifyOtp(email, otp);

      if (data.access_token) {
        // Store tokens and user data in localStorage
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token || "");

        // Create user object with available data
        const userData = {
          email: email,
          name: data.name || "",
          id: data.id || "",
          access_token: data.access_token,
          refresh_token: data.refresh_token || "",
        };

        localStorage.setItem("User", JSON.stringify(userData));

        // Update context
        setUser?.(userData);
        setToken?.(data.access_token);

        toast.success("OTP verified! You are now logged in.");

        if (onProceedToReset) {
          onProceedToReset(); // forgot password flow
        } else {
          closeModal(); // login flow
        }
      }
    } catch (err: any) {
      console.error("OTP verification failed:", err);
      setError(
        err.response?.data?.message ||
          "Invalid verification code. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && otp.length === 6) {
      handleSubmit();
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setResendCooldown(60);
    setError("");

    try {
      await service.resendOtp(email);
      toast.success("OTP resent to your email.");
    } catch (err: any) {
      console.error("Failed to resend OTP:", err);
      setError(
        err.response?.data?.message ||
          "Failed to resend code. Please try again."
      );
    }
  };

  return (
    <FlyoutLayout
      displayName={displayName}
      isOpen={isOpen}
      closeModal={closeModal}
      contentPadding="120px 32px"
      contentAlign="center"
      contentJustify="flex-start"
    >
      <div className={`${displayName}__form-container`}>
        <header className={`${displayName}__header`}>
          <h2 className={`${displayName}__title`}>
            We've sent a{" "}
            <em className={`${displayName}__title-highlight`}>one-time code</em>{" "}
            to <span className={`${displayName}__email`}>{email}</span>
          </h2>
          <p className={`${displayName}__subtitle`}>Enter 6-digit code</p>
        </header>

        <div className={`${displayName}__form`}>
          <div className={`${displayName}__input-container`}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              onKeyPress={handleKeyPress}
              className={`${displayName}__otp-input ${
                error ? `${displayName}__otp-input--error` : ""
              }`}
              disabled={isLoading}
              placeholder="Enter code"
            />
            {error && <div className={`${displayName}__error`}>{error}</div>}
          </div>

          <p className={`${displayName}__resend-text`}>
            Don't get the Code?{" "}
            <button
              type="button"
              className={`${displayName}__resend-link ${
                resendCooldown > 0
                  ? `${displayName}__resend-link--disabled`
                  : ""
              }`}
              onClick={handleResend}
              disabled={resendCooldown > 0}
            >
              {resendCooldown > 0
                ? `Resend OTP (${resendCooldown}s)`
                : "Resend OTP"}
            </button>
          </p>

          <div className={`${displayName}__actions`}>
            <button
              type="button"
              onClick={() => handleSubmit()}
              className={`${displayName}__submit ${
                isLoading ? `${displayName}__submit--loading` : ""
              }`}
              disabled={isLoading || otp.length !== 6}
            >
              {isLoading ? "Verifying..." : "Verify & Continue"}
            </button>

            {onBackToForgot && (
              <button
                type="button"
                className={`${displayName}__back-btn`}
                onClick={onBackToForgot}
              >
                Back
              </button>
            )}
          </div>
        </div>
      </div>
    </FlyoutLayout>
  );
};

export default OTPForm;
